<script setup lang="ts">
import { ref } from 'vue';
import { Code, Eye, Check, Copy } from '@lucide/vue';
import Card from './Card.vue';
import Button from './Button.vue';

const props = defineProps<{
  title: string;
  description?: string;
  htmlCode: string;
}>();

const activeTab = ref<'preview' | 'html'>('preview');
const copied = ref(false);

const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(props.htmlCode);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy', err);
  }
};
</script>

<template>
  <div class="component-showcase">
    <div class="showcase-header">
      <div class="showcase-info">
        <h3 class="babel-text-h3" style="margin-bottom: 4px;">{{ title }}</h3>
        <p v-if="description" class="babel-text-body" style="font-size: 0.875rem;">{{ description }}</p>
      </div>
      <div class="showcase-tabs">
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'preview' }" 
          @click="activeTab = 'preview'"
        >
          <Eye :size="16" /> Preview
        </button>
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'html' }" 
          @click="activeTab = 'html'"
        >
          <Code :size="16" /> HTML
        </button>
      </div>
    </div>

    <Card class="showcase-body">
      <div v-show="activeTab === 'preview'" class="showcase-preview">
        <slot />
      </div>
      
      <div v-show="activeTab === 'html'" class="showcase-code">
        <div class="code-actions">
          <Button variant="ghost" @click="copyCode" style="min-height: 32px; padding: 0 12px;">
            <Check v-if="copied" :size="16" class="babel-text-gold" />
            <Copy v-else :size="16" />
            <span style="font-size: 0.875rem;">{{ copied ? 'Copied!' : 'Copy HTML' }}</span>
          </Button>
        </div>
        <pre><code>{{ htmlCode }}</code></pre>
      </div>
    </Card>
  </div>
</template>

<style scoped lang="scss">
.component-showcase {
  display: flex;
  flex-direction: column;
  gap: var(--babel-space-3);
  margin-bottom: var(--babel-space-8);
}

.showcase-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--babel-space-4);
  flex-wrap: wrap;
}

.showcase-tabs {
  display: flex;
  background: rgba(255, 255, 255, 0.05);
  padding: 4px;
  border-radius: var(--babel-radius-md);
  border: 1px solid var(--babel-color-border);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  color: var(--babel-color-muted);
  font-family: var(--babel-font-sans);
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: var(--babel-radius-sm);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: var(--babel-color-white);
  }

  &.active {
    background: var(--babel-color-base);
    color: var(--babel-color-gold);
    box-shadow: var(--babel-shadow-sm);
  }
}

.showcase-body {
  padding: 0;
  overflow: hidden;
}

.showcase-preview {
  padding: var(--babel-space-6);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: radial-gradient(circle, rgba(212, 166, 61, 0.05) 0%, rgba(15, 15, 16, 0) 70%);
}

.showcase-code {
  position: relative;
  background: #000;
  padding: var(--babel-space-5);
  min-height: 200px;
  overflow-x: auto;
}

.code-actions {
  position: absolute;
  top: 12px;
  right: 12px;
}

pre {
  margin: 0;
  margin-top: var(--babel-space-4);
}

code {
  color: var(--babel-color-gold);
  font-family: monospace;
  font-size: 0.875rem;
  white-space: pre-wrap;
}
</style>
